import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useFormikContext, FieldArray } from 'formik';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Box, CustomInput } from 'atoms';
import { SLAB_MAX_ROWS, SLAB_MIN_ROWS, makeEmptySlab } from './warehouseRevenue';

const SlabsTable = styled.table`
	width: 100%;
	border-collapse: collapse;
	border: 1px solid #e5e7eb;
	border-radius: 6px;
	overflow: hidden;

	thead th {
		background: #1e293b;
		color: #fff;
		font-size: 11px;
		font-weight: 600;
		letter-spacing: 0.3px;
		text-align: left;
		padding: 10px 14px;
	}
	thead th.action {
		text-align: center;
		width: 90px;
	}
	tbody td {
		padding: 8px 14px;
		border-top: 1px solid #eef1f5;
		font-size: 13px;
		color: #1f2933;
		vertical-align: top;
	}
	tbody td.action {
		text-align: center;
		vertical-align: middle;
	}
`;

const RemoveSlabButton = styled.button`
	border: none;
	background: transparent;
	color: #ff4d4f;
	cursor: pointer;
	font-size: 16px;
	line-height: 1;

	&:disabled {
		color: #cbd5e1;
		cursor: not-allowed;
	}
`;

const AddSlabButton = styled.button`
	display: inline-flex;
	align-items: center;
	gap: 8px;
	border: 1px solid #18405d;
	background: #fff;
	color: #18405d;
	font-size: 13px;
	font-weight: 500;
	height: 36px;
	padding: 0 16px;
	border-radius: 4px;
	cursor: pointer;

	&:hover:not(:disabled) {
		background: #18405d;
		color: #fff;
	}
	&:disabled {
		border-color: #d9d9d9;
		color: #bfbfbf;
		cursor: not-allowed;
	}
`;

const TableFooterRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	margin-top: 12px;
`;

const RowCount = styled.span`
	font-size: 12px;
	color: #64748b;
`;

const ErrorText = styled.div`
	color: #ff4d4f;
	font-size: 12px;
	margin-top: 4px;
`;

/**
 * Slab configuration grid for the Per Order revenue model.
 */
export default function WarehouseSlabsField({ isViewMode }) {
	const { values, errors, touched, submitCount, setFieldValue } = useFormikContext();
	const slabs = values.slabs || [];

	// Show a row error once the field is touched or the user has attempted a submit
	const slabError = (index, field) => {
		const error = errors?.slabs?.[index]?.[field];
		const isTouched = submitCount > 0 || Boolean(touched?.slabs?.[index]?.[field]);
		return error && isTouched ? error : null;
	};

	// Array-level error (e.g. "Add at least one slab") arrives as a plain string
	const arrayError = typeof errors?.slabs === 'string' && submitCount > 0 ? errors.slabs : null;

	const handleAddSlab = () => {
		const last = slabs[slabs.length - 1];
		const nextMin = last && last.slab_max !== '' ? Number(last.slab_max) + 1 : 0;
		setFieldValue('slabs', [...slabs, makeEmptySlab(nextMin)]);
	};

	const handleRemoveSlab = (index) =>
		setFieldValue(
			'slabs',
			slabs.filter((_, i) => i !== index)
		);

	const renderCell = (index, field, placeholder) => (
		<>
			<CustomInput
				name={`slabs.${index}.${field}`}
				type="number"
				min={0}
				step="any"
				placeholder={placeholder}
				disabled={isViewMode}
				onKeyDown={(e) => {
					if (['-', '+', 'e', 'E'].includes(e.key)) {
						e.preventDefault();
					}
				}}
			/>
			{slabError(index, field) && <ErrorText>{slabError(index, field)}</ErrorText>}
		</>
	);

	return (
		<Box mb="8px">
			<FieldArray name="slabs">
				{() => (
					<>
						<SlabsTable>
							<thead>
								<tr>
									<th>SLAB RANGE (MIN)</th>
									<th>SLAB RANGE (MAX)</th>
									<th>RATE PER ORDER (₹)</th>
									{!isViewMode && <th className="action">ACTIONS</th>}
								</tr>
							</thead>
							<tbody>
								{slabs.map((slab, index) => (
									// eslint-disable-next-line react/no-array-index-key
									<tr key={`slab-${index}`}>
										<td>{renderCell(index, 'slab_min', '0')}</td>
										<td>{renderCell(index, 'slab_max', '0')}</td>
										<td>{renderCell(index, 'rate_per_order', '0')}</td>
										{!isViewMode && (
											<td className="action">
												<RemoveSlabButton
													type="button"
													aria-label={`Remove slab ${index + 1}`}
													disabled={slabs.length <= SLAB_MIN_ROWS}
													onClick={() => handleRemoveSlab(index)}
												>
													<DeleteOutlined />
												</RemoveSlabButton>
											</td>
										)}
									</tr>
								))}
							</tbody>
						</SlabsTable>

						{arrayError && <ErrorText>{arrayError}</ErrorText>}

						{!isViewMode && (
							<TableFooterRow>
								<AddSlabButton
									type="button"
									onClick={handleAddSlab}
									disabled={slabs.length >= SLAB_MAX_ROWS}
								>
									<PlusOutlined />
									Add Slab
								</AddSlabButton>
								<RowCount>
									{slabs.length} of {SLAB_MAX_ROWS} slabs
								</RowCount>
							</TableFooterRow>
						)}
					</>
				)}
			</FieldArray>
		</Box>
	);
}

WarehouseSlabsField.propTypes = {
	isViewMode: PropTypes.bool,
};

WarehouseSlabsField.defaultProps = {
	isViewMode: false,
};
